import { useTranslation } from "react-i18next"
import { ButtonLink } from "@/lib/ButtonLink"
import {
  PORTAL_ADMIN,
  PORTAL_DEPOSIT,
  PORTAL_EVENTS,
  PORTAL_PROJECTS,
  PUBLIC_DEPOSITS,
} from "@/app/router/portalRoutes"
import { isDepositsUiEnabled } from "@/features/deposits/depositsUiEnabled"
import type { NavItemKey } from "./navActiveMatchers"

export type NavbarActiveMap = Record<NavItemKey, boolean>

export type NavbarDesktopLinksProps = {
  activeMap: NavbarActiveMap
  headerTone: "transparent" | "solid"
  isAuthenticated: boolean
  hasAnyAdminAccess: boolean
  ariaLabel: string
}

export const NavbarDesktopLinks = ({
  activeMap,
  headerTone,
  isAuthenticated,
  hasAnyAdminAccess,
  ariaLabel,
}: NavbarDesktopLinksProps) => {
  const { t } = useTranslation()

  const renderLink = (path: string, labelKey: NavItemKey) => (
    <ButtonLink
      key={path}
      to={path}
      variant="navHeader"
      headerTone={headerTone}
      isActive={activeMap[labelKey]}
    >
      {t(`nav.${labelKey}`)}
    </ButtonLink>
  )

  return (
    <div className="hidden md:flex order-3 basis-full xl:order-none xl:basis-auto flex-1 min-w-0 justify-center items-center px-1 sm:px-2 pt-2 xl:pt-0 border-t border-gray-100/80 xl:border-t-0 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
      <nav className="flex items-center gap-0.5 sm:gap-1 shrink-0" aria-label={ariaLabel}>
        {renderLink("/profile", "profile")}
        {isAuthenticated ? (
          <ButtonLink to={PORTAL_EVENTS} variant="navHeader" headerTone={headerTone} isActive={activeMap.events}>
            {t("nav.events")}
          </ButtonLink>
        ) : (
          renderLink("/events", "events")
        )}
        {isAuthenticated ? (
          <ButtonLink to={PORTAL_PROJECTS} variant="navHeader" headerTone={headerTone} isActive={activeMap.projects}>
            {t("nav.projects")}
          </ButtonLink>
        ) : null}
        {isDepositsUiEnabled ? (
          <ButtonLink
            to={isAuthenticated ? PORTAL_DEPOSIT : PUBLIC_DEPOSITS}
            variant="navHeader"
            headerTone={headerTone}
            isActive={activeMap.deposit}
          >
            {t("nav.deposit")}
          </ButtonLink>
        ) : null}
        {isAuthenticated && hasAnyAdminAccess ? (
          <ButtonLink to={PORTAL_ADMIN} variant="navHeader" headerTone={headerTone} isActive={activeMap.admin}>
            {t("nav.admin")}
          </ButtonLink>
        ) : null}
        {!isAuthenticated ? renderLink("/about", "about") : null}
      </nav>
    </div>
  )
}
